import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '@/components/Layout';
import { API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { 
  Mail, 
  Send, 
  Settings, 
  History, 
  AlertCircle, 
  CheckCircle, 
  XCircle, 
  RefreshCw, 
  Eye,
  Save,
  TestTube,
  Users,
  Calendar,
  Loader2,
  Clock,
  Zap,
  Play,
  Plus,
  Trash2,
  MessageCircle,
  Phone
} from 'lucide-react';

const NotificationsPage = () => {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [savingAutoConfig, setSavingAutoConfig] = useState(false);
  const [runningNow, setRunningNow] = useState(false);
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  
  // Config state
  const [config, setConfig] = useState(null);
  
  // WhatsApp state
  const [whatsAppAlerts, setWhatsAppAlerts] = useState([]);
  const [whatsAppFilter, setWhatsAppFilter] = useState('7');
  const [testWhatsAppDialogOpen, setTestWhatsAppDialogOpen] = useState(false);
  const [testWhatsAppNumber, setTestWhatsAppNumber] = useState('');
  const [bulkWhatsAppDialogOpen, setBulkWhatsAppDialogOpen] = useState(false);
  const [bulkWhatsAppResult, setBulkWhatsAppResult] = useState(null);
  
  // Auto config state
  const [autoConfig, setAutoConfig] = useState({
    enabled: false,
    dias_recordatorio: [7, 3],
    hora_envio: 8,
    minuto_envio: 0,
    evitar_duplicados: true,
    emails_reporte: [],
    enviar_reporte_email: true,
    last_run: null,
    last_run_stats: null
  });
  const [newReportEmail, setNewReportEmail] = useState('');
  
  // Template state
  const [template, setTemplate] = useState({
    asunto: '',
    mensaje: '',
    nombre_remitente: 'Vaxprosalud'
  });
  
  // Alerts state
  const [alerts, setAlerts] = useState([]);
  const [alertsFilter, setAlertsFilter] = useState('7');
  
  // Logs state
  const [logs, setLogs] = useState([]);
  
  // Test email dialog
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  
  // Bulk send dialog
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const fetchConfig = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/notifications/config`, { withCredentials: true });
      setConfig(response.data);
    } catch (error) {
      console.error('Error loading config:', error);
    }
  }, []);

  const fetchAutoConfig = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/notifications/auto-config`, { withCredentials: true });
      setAutoConfig(response.data);
    } catch (error) {
      console.error('Error loading auto config:', error);
    }
  }, []);

  const fetchTemplate = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/notifications/email-template`, { withCredentials: true });
      setTemplate(response.data);
    } catch (error) {
      console.error('Error loading template:', error);
    }
  }, []);

  const fetchAlerts = useCallback(async () => {
    try {
      const dias = alertsFilter === 'all' ? null : parseInt(alertsFilter);
      const params = dias ? { dias_limite: dias } : {};
      const response = await axios.get(`${API}/alerts`, { 
        params,
        withCredentials: true 
      });
      // Filter only those with email
      const alertsWithEmail = response.data.filter(a => 
        a.paciente_email && ['PENDIENTE', 'VENCIDO'].includes(a.estado)
      );
      setAlerts(alertsWithEmail);
    } catch (error) {
      console.error('Error loading alerts:', error);
    }
  }, [alertsFilter]);

  const fetchLogs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/notifications/logs`, { 
        params: { limit: 100 },
        withCredentials: true 
      });
      setLogs(response.data);
    } catch (error) {
      console.error('Error loading logs:', error);
    }
  }, []);

  const fetchWhatsAppAlerts = useCallback(async () => {
    try {
      const dias = whatsAppFilter === 'all' ? null : parseInt(whatsAppFilter);
      const params = dias ? { dias_limite: dias } : {};
      const response = await axios.get(`${API}/alerts`, { 
        params,
        withCredentials: true 
      });
      // Filter only those with phone number
      const alertsWithPhone = response.data.filter(a => 
        a.paciente_telefono && ['PENDIENTE', 'VENCIDO'].includes(a.estado) && !a.whatsapp_sent
      );
      setWhatsAppAlerts(alertsWithPhone);
    } catch (error) {
      console.error('Error loading WhatsApp alerts:', error);
    }
  }, [whatsAppFilter]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchConfig(), fetchAutoConfig(), fetchTemplate(), fetchAlerts(), fetchLogs(), fetchWhatsAppAlerts()]);
      setLoading(false);
    };
    loadData();
  }, [fetchConfig, fetchAutoConfig, fetchTemplate, fetchAlerts, fetchLogs, fetchWhatsAppAlerts]);

  useEffect(() => {
    fetchAlerts();
  }, [alertsFilter, fetchAlerts]);

  useEffect(() => {
    fetchWhatsAppAlerts();
  }, [whatsAppFilter, fetchWhatsAppAlerts]);

  const handleSaveTemplate = async () => {
    setSavingTemplate(true);
    try {
      await axios.post(`${API}/notifications/email-template`, null, {
        params: {
          asunto: template.asunto,
          mensaje: template.mensaje,
          nombre_remitente: template.nombre_remitente
        },
        withCredentials: true
      });
      toast.success('Plantilla guardada exitosamente');
    } catch (error) {
      toast.error('Error al guardar la plantilla');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleSaveAutoConfig = async () => {
    setSavingAutoConfig(true);
    try {
      await axios.post(`${API}/notifications/auto-config`, autoConfig, { withCredentials: true });
      toast.success('Configuración automática guardada');
      fetchAutoConfig();
    } catch (error) {
      toast.error('Error al guardar configuración');
    } finally {
      setSavingAutoConfig(false);
    }
  };

  const handleRunNow = async () => {
    setRunningNow(true);
    try {
      const response = await axios.post(`${API}/notifications/auto-run-now`, {}, { withCredentials: true });
      toast.success(`Enviados: ${response.data.enviados}, Fallidos: ${response.data.fallidos}`);
      fetchAutoConfig();
      fetchAlerts();
      fetchLogs();
    } catch (error) {
      toast.error('Error al ejecutar notificaciones');
    } finally {
      setRunningNow(false);
    }
  };

  const handleAddReportEmail = () => {
    if (!newReportEmail || !newReportEmail.includes('@')) {
      toast.error('Ingrese un correo válido');
      return;
    }
    if (autoConfig.emails_reporte.includes(newReportEmail)) {
      toast.error('Este correo ya está en la lista');
      return;
    }
    setAutoConfig({
      ...autoConfig,
      emails_reporte: [...autoConfig.emails_reporte, newReportEmail]
    });
    setNewReportEmail('');
  };

  const handleRemoveReportEmail = (email) => {
    setAutoConfig({
      ...autoConfig,
      emails_reporte: autoConfig.emails_reporte.filter(e => e !== email)
    });
  };

  const handleToggleDia = (dia) => {
    const dias = autoConfig.dias_recordatorio || [];
    if (dias.includes(dia)) {
      setAutoConfig({
        ...autoConfig,
        dias_recordatorio: dias.filter(d => d !== dia)
      });
    } else {
      setAutoConfig({
        ...autoConfig,
        dias_recordatorio: [...dias, dia].sort((a, b) => b - a)
      });
    }
  };

  const handleSendTest = async () => {
    if (!testEmail) {
      toast.error('Ingrese un correo de destino');
      return;
    }
    
    setTestingEmail(true);
    try {
      await axios.post(`${API}/notifications/send-email-test`, null, {
        params: { email_destino: testEmail },
        withCredentials: true
      });
      toast.success(`Correo de prueba enviado a ${testEmail}`);
      setTestDialogOpen(false);
      setTestEmail('');
      fetchLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar correo de prueba');
    } finally {
      setTestingEmail(false);
    }
  };

  const handleSendIndividual = async (alertId, patientName) => {
    try {
      await axios.post(`${API}/notifications/send-email-reminder/${alertId}`, {}, { withCredentials: true });
      toast.success(`Recordatorio enviado a ${patientName}`);
      fetchAlerts();
      fetchLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar recordatorio');
    }
  };

  const handleSendBulk = async () => {
    setSending(true);
    setBulkResult(null);
    
    try {
      const dias = alertsFilter === 'all' ? 365 : parseInt(alertsFilter);
      const response = await axios.post(`${API}/notifications/send-bulk-email`, null, {
        params: { dias_limite: dias },
        withCredentials: true
      });
      
      setBulkResult(response.data);
      toast.success(`Enviados: ${response.data.enviados}, Fallidos: ${response.data.fallidos}`);
      fetchAlerts();
      fetchLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar recordatorios masivos');
    } finally {
      setSending(false);
    }
  };

  // WhatsApp functions
  const handleSendWhatsAppIndividual = async (alertId, patientName) => {
    try {
      await axios.post(`${API}/notifications/send-whatsapp-reminder/${alertId}`, {}, { withCredentials: true });
      toast.success(`WhatsApp enviado a ${patientName}`);
      fetchWhatsAppAlerts();
      fetchLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar WhatsApp');
    }
  };

  const handleSendWhatsAppTest = async () => {
    if (!testWhatsAppNumber) {
      toast.error('Ingrese un número de teléfono');
      return;
    }
    
    setSendingWhatsApp(true);
    try {
      await axios.post(`${API}/notifications/send-whatsapp-test`, null, {
        params: { telefono: testWhatsAppNumber },
        withCredentials: true
      });
      toast.success(`WhatsApp de prueba enviado a ${testWhatsAppNumber}`);
      setTestWhatsAppDialogOpen(false);
      setTestWhatsAppNumber('');
      fetchLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar WhatsApp de prueba');
    } finally {
      setSendingWhatsApp(false);
    }
  };

  const handleSendWhatsAppBulk = async () => {
    setSendingWhatsApp(true);
    setBulkWhatsAppResult(null);
    
    try {
      const dias = whatsAppFilter === 'all' ? 365 : parseInt(whatsAppFilter);
      const response = await axios.post(`${API}/notifications/send-bulk-whatsapp`, null, {
        params: { dias_limite: dias },
        withCredentials: true
      });
      
      setBulkWhatsAppResult(response.data);
      toast.success(`Enviados: ${response.data.enviados}, Fallidos: ${response.data.fallidos}`);
      fetchWhatsAppAlerts();
      fetchLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar WhatsApp masivos');
    } finally {
      setSendingWhatsApp(false);
    }
  };

  const getStatusBadge = (estado, tipo) => {
    const isAuto = tipo === 'EMAIL_AUTO';
    switch (estado) {
      case 'ENVIADO':
        return (
          <Badge className="bg-emerald-100 text-emerald-700">
            <CheckCircle className="w-3 h-3 mr-1" /> 
            {isAuto ? 'Auto' : 'Enviado'}
          </Badge>
        );
      case 'FALLIDO':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" /> Fallido</Badge>;
      default:
        return <Badge className="bg-slate-100 text-slate-700">{estado}</Badge>;
    }
  };

  const getAlertStatusBadge = (estado, dias) => {
    if (dias < 0) {
      return <Badge className="bg-red-100 text-red-700">Vencido ({Math.abs(dias)} días)</Badge>;
    } else if (dias <= 3) {
      return <Badge className="bg-amber-100 text-amber-700">Urgente ({dias} días)</Badge>;
    } else if (dias <= 7) {
      return <Badge className="bg-blue-100 text-blue-700">Próximo ({dias} días)</Badge>;
    }
    return <Badge className="bg-slate-100 text-slate-700">{dias} días</Badge>;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Loader2 className="w-10 h-10 mx-auto mb-4 animate-spin text-blue-500" />
            <p className="text-slate-500">Cargando configuración...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="notifications-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Notificaciones</h1>
            <p className="text-slate-500">Gestiona recordatorios de vacunación por email y WhatsApp</p>
          </div>
          
          <div className="flex flex-wrap items-center gap-2">
            {autoConfig?.enabled && (
              <Badge className="bg-blue-100 text-blue-700">
                <Zap className="w-4 h-4 mr-1" />
                Auto Activo
              </Badge>
            )}
            {config?.email_configured ? (
              <Badge className="bg-emerald-100 text-emerald-700">
                <CheckCircle className="w-4 h-4 mr-1" />
                SMTP
              </Badge>
            ) : (
              <Badge className="bg-red-100 text-red-700">
                <AlertCircle className="w-4 h-4 mr-1" />
                SMTP
              </Badge>
            )}
            {config?.twilio_configured ? (
              <Badge className="bg-green-100 text-green-700">
                <CheckCircle className="w-4 h-4 mr-1" />
                WhatsApp
              </Badge>
            ) : (
              <Badge className="bg-red-100 text-red-700">
                <AlertCircle className="w-4 h-4 mr-1" />
                WhatsApp
              </Badge>
            )}
          </div>
        </div>

        {/* Main Content */}
        <Tabs defaultValue="auto" className="space-y-4">
          <TabsList className="grid w-full grid-cols-5 lg:w-auto lg:inline-grid">
            <TabsTrigger value="auto" className="gap-2">
              <Zap className="w-4 h-4" />
              <span className="hidden sm:inline">Automático</span>
            </TabsTrigger>
            <TabsTrigger value="send" className="gap-2">
              <Mail className="w-4 h-4" />
              <span className="hidden sm:inline">Email</span>
            </TabsTrigger>
            <TabsTrigger value="whatsapp" className="gap-2">
              <MessageCircle className="w-4 h-4" />
              <span className="hidden sm:inline">WhatsApp</span>
            </TabsTrigger>
            <TabsTrigger value="template" className="gap-2">
              <Settings className="w-4 h-4" />
              <span className="hidden sm:inline">Plantilla</span>
            </TabsTrigger>
            <TabsTrigger value="history" className="gap-2">
              <History className="w-4 h-4" />
              <span className="hidden sm:inline">Historial</span>
            </TabsTrigger>
          </TabsList>

          {/* Auto Tab */}
          <TabsContent value="auto" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Zap className="w-5 h-5" />
                      Notificaciones Automáticas
                    </CardTitle>
                    <CardDescription>
                      Configura el envío automático de recordatorios según el esquema de vacunación
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="auto-enabled" className="text-sm">Activar</Label>
                    <Switch
                      id="auto-enabled"
                      checked={autoConfig.enabled}
                      onCheckedChange={(checked) => setAutoConfig({ ...autoConfig, enabled: checked })}
                      data-testid="auto-enabled-switch"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Días de recordatorio */}
                <div className="space-y-3">
                  <Label>Días antes para enviar recordatorio</Label>
                  <p className="text-sm text-slate-500">Selecciona los días antes de la fecha de vacunación para enviar recordatorios</p>
                  <div className="flex flex-wrap gap-2">
                    {[1, 2, 3, 5, 7, 10, 14, 30].map(dia => (
                      <Button
                        key={dia}
                        variant={autoConfig.dias_recordatorio?.includes(dia) ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleToggleDia(dia)}
                        data-testid={`dia-btn-${dia}`}
                      >
                        {dia} {dia === 1 ? 'día' : 'días'}
                      </Button>
                    ))}
                  </div>
                  {autoConfig.dias_recordatorio?.length > 0 && (
                    <p className="text-sm text-blue-600">
                      Se enviará recordatorio: {autoConfig.dias_recordatorio.sort((a,b) => b-a).join(', ')} días antes
                    </p>
                  )}
                </div>

                {/* Hora de envío */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Hora de envío</Label>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-slate-400" />
                      <Select 
                        value={String(autoConfig.hora_envio)} 
                        onValueChange={(v) => setAutoConfig({ ...autoConfig, hora_envio: parseInt(v) })}
                      >
                        <SelectTrigger className="w-24" data-testid="hora-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 24 }, (_, i) => (
                            <SelectItem key={i} value={String(i)}>{String(i).padStart(2, '0')}:00</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <span className="text-slate-500">hrs</span>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Evitar duplicados</Label>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={autoConfig.evitar_duplicados}
                        onCheckedChange={(checked) => setAutoConfig({ ...autoConfig, evitar_duplicados: checked })}
                        data-testid="evitar-duplicados-switch"
                      />
                      <span className="text-sm text-slate-500">No enviar si ya recibió recordatorio manual</span>
                    </div>
                  </div>
                </div>

                {/* Emails de reporte */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Correos para reporte diario</Label>
                      <p className="text-sm text-slate-500">Recibe un resumen diario de los envíos automáticos</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={autoConfig.enviar_reporte_email}
                        onCheckedChange={(checked) => setAutoConfig({ ...autoConfig, enviar_reporte_email: checked })}
                      />
                      <span className="text-sm">Enviar reporte</span>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      value={newReportEmail}
                      onChange={(e) => setNewReportEmail(e.target.value)}
                      placeholder="correo@ejemplo.com"
                      className="flex-1"
                      data-testid="new-report-email"
                    />
                    <Button variant="outline" onClick={handleAddReportEmail}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {autoConfig.emails_reporte?.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {autoConfig.emails_reporte.map((email) => (
                        <Badge key={email} variant="secondary" className="gap-1 pr-1">
                          {email}
                          <button 
                            onClick={() => handleRemoveReportEmail(email)}
                            className="ml-1 hover:bg-slate-200 rounded p-0.5"
                          >
                            <XCircle className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Last run stats */}
                {autoConfig.last_run && (
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <h4 className="font-medium text-slate-700 mb-2">Último envío automático</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                      <div>
                        <div className="text-2xl font-bold text-slate-900">
                          {autoConfig.last_run_stats?.enviados || 0}
                        </div>
                        <div className="text-xs text-emerald-600">Enviados</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-slate-900">
                          {autoConfig.last_run_stats?.fallidos || 0}
                        </div>
                        <div className="text-xs text-red-600">Fallidos</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-slate-900">
                          {autoConfig.last_run_stats?.total_alertas || 0}
                        </div>
                        <div className="text-xs text-slate-500">Total</div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-700">
                          {new Date(autoConfig.last_run).toLocaleString('es-CL', {
                            day: '2-digit',
                            month: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                        <div className="text-xs text-slate-500">Fecha</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-2 pt-4 border-t">
                  <Button onClick={handleSaveAutoConfig} disabled={savingAutoConfig} data-testid="save-auto-config">
                    {savingAutoConfig ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Guardar Configuración
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    onClick={handleRunNow} 
                    disabled={runningNow || !config?.email_configured}
                    data-testid="run-now-btn"
                  >
                    {runningNow ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Play className="w-4 h-4 mr-2" />
                    )}
                    Ejecutar Ahora
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Send Tab (Manual) */}
          <TabsContent value="send" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      Envío Manual
                    </CardTitle>
                    <CardDescription>
                      Pacientes con vacunaciones pendientes que tienen correo registrado
                    </CardDescription>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Select value={alertsFilter} onValueChange={setAlertsFilter}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Filtrar por días" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="3">Próximos 3 días</SelectItem>
                        <SelectItem value="7">Próximos 7 días</SelectItem>
                        <SelectItem value="15">Próximos 15 días</SelectItem>
                        <SelectItem value="30">Próximos 30 días</SelectItem>
                        <SelectItem value="all">Todos</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => fetchAlerts()}
                      data-testid="refresh-alerts-btn"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {alerts.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Mail className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No hay alertas pendientes con correo en este período</p>
                  </div>
                ) : (
                  <>
                    <div className="mb-4 flex justify-end">
                      <Button 
                        onClick={() => setBulkDialogOpen(true)}
                        disabled={!config?.email_configured}
                        data-testid="send-bulk-btn"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Enviar a Todos ({alerts.length})
                      </Button>
                    </div>
                    
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Paciente</TableHead>
                            <TableHead className="hidden md:table-cell">Email</TableHead>
                            <TableHead>Vacuna</TableHead>
                            <TableHead className="hidden sm:table-cell">Próxima Dosis</TableHead>
                            <TableHead>Estado</TableHead>
                            <TableHead className="text-right">Acción</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {alerts.map((alert) => (
                            <TableRow key={alert.id}>
                              <TableCell className="font-medium">
                                {alert.paciente_nombre}
                              </TableCell>
                              <TableCell className="hidden md:table-cell text-slate-500">
                                {alert.paciente_email}
                              </TableCell>
                              <TableCell>{alert.vacuna}</TableCell>
                              <TableCell className="hidden sm:table-cell">
                                <div className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4 text-slate-400" />
                                  {alert.fecha_proxima}
                                </div>
                              </TableCell>
                              <TableCell>
                                {getAlertStatusBadge(alert.estado, alert.dias_restantes)}
                              </TableCell>
                              <TableCell className="text-right">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleSendIndividual(alert.id, alert.paciente_nombre)}
                                  disabled={!config?.email_configured}
                                  data-testid={`send-individual-${alert.id}`}
                                >
                                  <Mail className="w-4 h-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* WhatsApp Tab */}
          <TabsContent value="whatsapp" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <MessageCircle className="w-5 h-5 text-green-600" />
                      WhatsApp
                    </CardTitle>
                    <CardDescription>
                      Pacientes con vacunaciones pendientes que tienen teléfono registrado
                    </CardDescription>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Select value={whatsAppFilter} onValueChange={setWhatsAppFilter}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Filtrar por días" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="3">Próximos 3 días</SelectItem>
                        <SelectItem value="7">Próximos 7 días</SelectItem>
                        <SelectItem value="15">Próximos 15 días</SelectItem>
                        <SelectItem value="30">Próximos 30 días</SelectItem>
                        <SelectItem value="all">Todos</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => fetchWhatsAppAlerts()}
                      data-testid="refresh-whatsapp-alerts-btn"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {!config?.twilio_configured ? (
                  <div className="text-center py-8 text-slate-500">
                    <AlertCircle className="w-12 h-12 mx-auto mb-3 text-amber-500" />
                    <p className="font-medium text-amber-700">Twilio no configurado</p>
                    <p className="text-sm">Contacta al administrador para configurar las credenciales de Twilio</p>
                  </div>
                ) : whatsAppAlerts.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No hay alertas pendientes con teléfono en este período</p>
                  </div>
                ) : (
                  <>
                    <div className="mb-4 flex justify-between items-center">
                      <Button 
                        variant="outline"
                        onClick={() => setTestWhatsAppDialogOpen(true)}
                        data-testid="test-whatsapp-btn"
                      >
                        <TestTube className="w-4 h-4 mr-2" />
                        Enviar Prueba
                      </Button>
                      <Button 
                        onClick={() => setBulkWhatsAppDialogOpen(true)}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid="send-bulk-whatsapp-btn"
                      >
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Enviar a Todos ({whatsAppAlerts.length})
                      </Button>
                    </div>
                    
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Paciente</TableHead>
                            <TableHead className="hidden md:table-cell">Teléfono</TableHead>
                            <TableHead>Vacuna</TableHead>
                            <TableHead className="hidden sm:table-cell">Próxima Dosis</TableHead>
                            <TableHead>Estado</TableHead>
                            <TableHead className="text-right">Acción</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {whatsAppAlerts.map((alert) => (
                            <TableRow key={alert.id}>
                              <TableCell className="font-medium">
                                {alert.paciente_nombre}
                              </TableCell>
                              <TableCell className="hidden md:table-cell text-slate-500">
                                {alert.paciente_telefono}
                              </TableCell>
                              <TableCell>{alert.vacuna}</TableCell>
                              <TableCell className="hidden sm:table-cell">
                                <div className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4 text-slate-400" />
                                  {alert.fecha_proxima}
                                </div>
                              </TableCell>
                              <TableCell>
                                {getAlertStatusBadge(alert.estado, alert.dias_restantes)}
                              </TableCell>
                              <TableCell className="text-right">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-green-300 text-green-700 hover:bg-green-50"
                                  onClick={() => handleSendWhatsAppIndividual(alert.id, alert.paciente_nombre)}
                                  data-testid={`send-whatsapp-${alert.id}`}
                                >
                                  <MessageCircle className="w-4 h-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Template Tab */}
          <TabsContent value="template" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Plantilla de Recordatorio
                </CardTitle>
                <CardDescription>
                  Personaliza el mensaje que recibirán los pacientes. Usa {'{nombre}'}, {'{vacuna}'}, {'{dosis}'}, {'{fecha_proxima}'} como variables.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="nombre_remitente">Nombre del Remitente</Label>
                  <Input
                    id="nombre_remitente"
                    value={template.nombre_remitente || ''}
                    onChange={(e) => setTemplate({ ...template, nombre_remitente: e.target.value })}
                    placeholder="Ej: Vaxprosalud"
                    data-testid="template-sender-name"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="asunto">Asunto del Correo</Label>
                  <Input
                    id="asunto"
                    value={template.asunto || ''}
                    onChange={(e) => setTemplate({ ...template, asunto: e.target.value })}
                    placeholder="Ej: Recordatorio de Vacunación - {vacuna}"
                    data-testid="template-subject"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="mensaje">Mensaje</Label>
                  <Textarea
                    id="mensaje"
                    value={template.mensaje || ''}
                    onChange={(e) => setTemplate({ ...template, mensaje: e.target.value })}
                    rows={8}
                    placeholder="Escriba el mensaje del recordatorio..."
                    data-testid="template-message"
                  />
                </div>
                
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button 
                    onClick={handleSaveTemplate}
                    disabled={savingTemplate}
                    data-testid="save-template-btn"
                  >
                    {savingTemplate ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Guardar Plantilla
                  </Button>
                  
                  <Button 
                    variant="outline"
                    onClick={() => setTestDialogOpen(true)}
                    disabled={!config?.email_configured}
                    data-testid="test-template-btn"
                  >
                    <TestTube className="w-4 h-4 mr-2" />
                    Enviar Prueba
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            {/* Preview Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="w-5 h-5" />
                  Vista Previa
                </CardTitle>
                <CardDescription>
                  Así se verá el correo con datos de ejemplo
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="bg-slate-50 p-4 rounded-lg border">
                  <div className="text-sm text-slate-500 mb-2">
                    <strong>De:</strong> {template.nombre_remitente || 'Vaxprosalud'} &lt;notificaciones@ejemplo.com&gt;
                  </div>
                  <div className="text-sm text-slate-500 mb-4">
                    <strong>Asunto:</strong> {(template.asunto || 'Recordatorio de Vacunación - {vacuna}')
                      .replace('{nombre}', 'Juan Pérez')
                      .replace('{vacuna}', 'Influenza')
                      .replace('{dosis}', 'Dosis Anual')
                      .replace('{fecha_proxima}', '15/01/2026')}
                  </div>
                  <div className="bg-white p-4 rounded border whitespace-pre-wrap text-sm">
                    {(template.mensaje || 'Estimado/a {nombre},\n\nLe recordamos que tiene pendiente su {dosis} de {vacuna}.\n\nFecha sugerida: {fecha_proxima}\n\nAtentamente,\n{nombre_remitente}')
                      .replace('{nombre}', 'Juan Pérez')
                      .replace('{vacuna}', 'Influenza')
                      .replace('{dosis}', 'Dosis Anual')
                      .replace('{fecha_proxima}', '15/01/2026')
                      .replace('{nombre_remitente}', template.nombre_remitente || 'Vaxprosalud')}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <History className="w-5 h-5" />
                      Historial de Notificaciones
                    </CardTitle>
                    <CardDescription>
                      Últimas notificaciones enviadas (manuales y automáticas)
                    </CardDescription>
                  </div>
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={fetchLogs}
                    data-testid="refresh-logs-btn"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {logs.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <History className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No hay notificaciones enviadas aún</p>
                  </div>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Fecha</TableHead>
                          <TableHead>Paciente</TableHead>
                          <TableHead className="hidden md:table-cell">Email</TableHead>
                          <TableHead>Tipo</TableHead>
                          <TableHead>Estado</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {logs.map((log) => (
                          <TableRow key={log.id}>
                            <TableCell className="text-sm text-slate-500">
                              {new Date(log.created_at).toLocaleString('es-CL', {
                                day: '2-digit',
                                month: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </TableCell>
                            <TableCell className="font-medium">
                              {log.paciente_nombre}
                            </TableCell>
                            <TableCell className="hidden md:table-cell text-slate-500">
                              {log.email || log.telefono}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">
                                {log.tipo === 'EMAIL_AUTO' ? (
                                  <><Zap className="w-3 h-3 mr-1" /> Auto</>
                                ) : log.tipo === 'EMAIL' ? (
                                  <><Mail className="w-3 h-3 mr-1" /> Manual</>
                                ) : log.tipo}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {getStatusBadge(log.estado, log.tipo)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Test Email Dialog */}
        <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Enviar Correo de Prueba</DialogTitle>
              <DialogDescription>
                Envía un correo de prueba con la plantilla actual para verificar cómo se ve.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label htmlFor="test-email">Correo de destino</Label>
              <Input
                id="test-email"
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="ejemplo@correo.com"
                className="mt-2"
                data-testid="test-email-input"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setTestDialogOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSendTest} disabled={testingEmail}>
                {testingEmail ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Enviar Prueba
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Send Dialog */}
        <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Enviar Recordatorios Masivos</DialogTitle>
              <DialogDescription>
                Se enviarán recordatorios a {alerts.length} pacientes con alertas pendientes.
              </DialogDescription>
            </DialogHeader>
            
            {bulkResult ? (
              <div className="py-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-emerald-50 p-4 rounded-lg text-center">
                    <div className="text-2xl font-bold text-emerald-600">{bulkResult.enviados}</div>
                    <div className="text-sm text-emerald-700">Enviados</div>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg text-center">
                    <div className="text-2xl font-bold text-red-600">{bulkResult.fallidos}</div>
                    <div className="text-sm text-red-700">Fallidos</div>
                  </div>
                </div>
                
                {bulkResult.detalles && bulkResult.detalles.length > 0 && (
                  <div className="max-h-40 overflow-y-auto border rounded p-2 text-sm">
                    {bulkResult.detalles.map((d, i) => (
                      <div key={`${d.paciente}-${i}`} className="flex items-center justify-between py-1 border-b last:border-0">
                        <span className="truncate">{d.paciente}</span>
                        {d.estado === 'ENVIADO' ? (
                          <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="py-4">
                <p className="text-slate-600">
                  ¿Está seguro de enviar recordatorios por correo a todos los pacientes listados?
                </p>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => { setBulkDialogOpen(false); setBulkResult(null); }}>
                {bulkResult ? 'Cerrar' : 'Cancelar'}
              </Button>
              {!bulkResult && (
                <Button onClick={handleSendBulk} disabled={sending}>
                  {sending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Enviar a Todos
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Test WhatsApp Dialog */}
        <Dialog open={testWhatsAppDialogOpen} onOpenChange={setTestWhatsAppDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-green-600" />
                Enviar WhatsApp de Prueba
              </DialogTitle>
              <DialogDescription>
                Envía un mensaje de prueba para verificar la configuración de WhatsApp.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label htmlFor="test-whatsapp-number">Número de teléfono</Label>
              <Input
                id="test-whatsapp-number"
                type="tel"
                value={testWhatsAppNumber}
                onChange={(e) => setTestWhatsAppNumber(e.target.value)}
                placeholder="+56912345678"
                className="mt-2"
                data-testid="test-whatsapp-input"
              />
              <p className="text-sm text-slate-500 mt-2">
                Incluye el código de país (ej: +56 para Chile)
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setTestWhatsAppDialogOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSendWhatsAppTest} disabled={sendingWhatsApp} className="bg-green-600 hover:bg-green-700">
                {sendingWhatsApp ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <MessageCircle className="w-4 h-4 mr-2" />
                )}
                Enviar Prueba
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk WhatsApp Dialog */}
        <Dialog open={bulkWhatsAppDialogOpen} onOpenChange={setBulkWhatsAppDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-green-600" />
                Enviar WhatsApp Masivo
              </DialogTitle>
              <DialogDescription>
                Se enviarán mensajes a {whatsAppAlerts.length} pacientes con alertas pendientes.
              </DialogDescription>
            </DialogHeader>
            
            {bulkWhatsAppResult ? (
              <div className="py-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-green-50 p-4 rounded-lg text-center">
                    <div className="text-2xl font-bold text-green-600">{bulkWhatsAppResult.enviados}</div>
                    <div className="text-sm text-green-700">Enviados</div>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg text-center">
                    <div className="text-2xl font-bold text-red-600">{bulkWhatsAppResult.fallidos}</div>
                    <div className="text-sm text-red-700">Fallidos</div>
                  </div>
                </div>
                
                {bulkWhatsAppResult.detalles && bulkWhatsAppResult.detalles.length > 0 && (
                  <div className="max-h-40 overflow-y-auto border rounded p-2 text-sm">
                    {bulkWhatsAppResult.detalles.map((d, i) => (
                      <div key={`${d.paciente}-wa-${i}`} className="flex items-center justify-between py-1 border-b last:border-0">
                        <span className="truncate">{d.paciente}</span>
                        {d.estado === 'ENVIADO' ? (
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="py-4">
                <p className="text-slate-600">
                  ¿Está seguro de enviar mensajes de WhatsApp a todos los pacientes listados?
                </p>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => { setBulkWhatsAppDialogOpen(false); setBulkWhatsAppResult(null); }}>
                {bulkWhatsAppResult ? 'Cerrar' : 'Cancelar'}
              </Button>
              {!bulkWhatsAppResult && (
                <Button onClick={handleSendWhatsAppBulk} disabled={sendingWhatsApp} className="bg-green-600 hover:bg-green-700">
                  {sendingWhatsApp ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <MessageCircle className="w-4 h-4 mr-2" />
                  )}
                  Enviar a Todos
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default NotificationsPage;
